import { fetchBlob, getJson, pathSegment, putJson } from "../client";
import { RESUME_BASE } from "./base";

/** A resume, how it is set, and its saved versions. */

/** One job the person held. `resume_api::resume::ResumeRole`. */
export interface ResumeRole {
    company: string;
    title: string;
    /** As the person writes it: `2021-03`, `2021`. */
    start?: string | null;
    /** As the person writes it, `present` included. */
    end?: string | null;
    location?: string | null;
    bullets: string[];
}

export interface ResumeEducation {
    school: string;
    degree?: string | null;
    year?: string | null;
}

/** One certification or license the person holds. `resume_api::resume::ResumeCertification`. */
export interface ResumeCertification {
    name: string;
    /** Who gave it: a body, a company or a state. */
    issuer?: string | null;
    year?: string | null;
}

/** A resume, as its fields. `resume_api::resume::Resume`. */
export interface Resume {
    name: string;
    headline?: string | null;
    email?: string | null;
    phone?: string | null;
    location?: string | null;
    links: string[];
    summary?: string | null;
    skills: string[];
    experience: ResumeRole[];
    education: ResumeEducation[];
    /** Left out by the service when there are none. */
    certifications?: ResumeCertification[];
}

export type Paper = "LETTER" | "A4";
export type Typeface = "HELVETICA" | "TIMES";
export type Density = "COMFORTABLE" | "COMPACT";
export type DateStyle = "NUMERIC" | "MONTH_YEAR";
export type FormatSection = "SUMMARY" | "SKILLS" | "EXPERIENCE" | "EDUCATION" | "CERTIFICATIONS";

/** How a resume is set on the page. `resume_api::format::ResumeFormat`. */
export interface ResumeFormat {
    paper: Paper;
    typeface: Typeface;
    density: Density;
    dates: DateStyle;
    /** Every section, once each, in the order they are set. */
    sections: FormatSection[];
}

export interface ResumeSource {
    upload_id: string;
    filename?: string | null;
}

/** One saved version of a person's resume, whole. `resume_api::responses::ResumeVersion`. */
export interface ResumeVersion {
    user_id: string;
    /** Counted from one. */
    version: number;
    sha256: string;
    saved_at: string;
    source?: ResumeSource | null;
    resume: Resume;
    format: ResumeFormat;
    /** The job title held now or last, as the resume service reads it from this version. */
    latest_title?: string | null;
}

/** A version the caller saved, the current one or an earlier one. */
export async function getResumeVersion(version: number): Promise<ResumeVersion> {
    return getJson(`${RESUME_BASE}/versions/${pathSegment(String(version))}`);
}

/** Every section, once each, in the order a resume without a format of its own is set in. */
export const DEFAULT_SECTIONS: readonly FormatSection[] = [
    "SUMMARY",
    "SKILLS",
    "EXPERIENCE",
    "EDUCATION",
    "CERTIFICATIONS",
];

/** The format a resume is set in when it has none of its own. `ResumeFormat::default`. */
export const DEFAULT_FORMAT: ResumeFormat = {
    paper: "LETTER",
    typeface: "HELVETICA",
    density: "COMFORTABLE",
    dates: "NUMERIC",
    sections: [...DEFAULT_SECTIONS],
};

/** The caller's resume as it stands. 404 until they have saved one. */
export function getBaseline(): Promise<ResumeVersion> {
    return getJson(`${RESUME_BASE}/baseline`);
}

/** A new version of the caller's resume, whole. `resume_api::requests::SaveResumeRequest`. */
export interface SaveResumeRequest {
    resume: Resume;
    format: ResumeFormat;
    /** The upload this version was imported from, when it was. */
    upload_id?: string | null;
}

/** Saves a new version; saving what is already the newest changes nothing. */
export function saveBaseline(request: SaveResumeRequest): Promise<ResumeVersion> {
    return putJson(`${RESUME_BASE}/baseline`, request);
}

/** The caller's resume as it stands, as the PDF a company is sent. */
export function getBaselinePdf(): Promise<Blob> {
    return fetchBlob(`${RESUME_BASE}/baseline/pdf`);
}

/** A resume and a format set as the PDF would be, without saving either. */
export function previewPdf(resume: Resume, format: ResumeFormat): Promise<Blob> {
    return fetchBlob(`${RESUME_BASE}/preview`, { resume, format });
}
